/**
 * OIDC Authentication Hook
 *
 * Story 5.6: Admin OIDC Authentication
 *
 * Provides a consistent interface for authentication state and actions.
 */

import { useMemo } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { getConfigSync } from "../config";
import { extractSovraUserId, isValidJwtFormat } from "../utils/tokenUtils";

/**
 * User information from OIDC token
 */
export interface AuthUser {
  /** Subject claim (unique user identifier from IDP) */
  sub: string;
  /** Sovra user ID (from custom claim or sub) */
  sovra_user_id: string | null;
  /** User's display name */
  name: string;
  /** User's email address */
  email: string;
  /** User's profile picture URL */
  picture?: string;
}

/**
 * Authentication hook return type
 */
export interface UseAuthReturn {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is loading */
  isLoading: boolean;
  /** Authenticated user information */
  user: AuthUser | null;
  /** Get access token for API calls */
  getAccessTokenSilently: () => Promise<string>;
  /** Redirect to login page */
  loginWithRedirect: (options?: {
    authorizationParams?: { screen_hint?: string };
  }) => Promise<void>;
  /** Log out the user */
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  /** Authentication error, if any */
  error: Error | undefined;
}

/**
 * OIDC Authentication Hook
 *
 * Provides access to authentication context with a consistent interface.
 *
 * @example
 * ```tsx
 * const { isAuthenticated, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <button onClick={loginWithRedirect}>Login</button>;
 * }
 *
 * const token = await getAccessTokenSilently();
 * // Use token for API calls
 * ```
 */
export const useAuth = (): UseAuthReturn => {
  const config = getConfigSync();
  const oidcAuth = useOidcAuth();

  // Extract sovra_user_id from access token if available
  const sovraUserId = useMemo(() => {
    if (oidcAuth.user?.access_token) {
      return extractSovraUserId(oidcAuth.user.access_token);
    }
    return null;
  }, [oidcAuth.user?.access_token]);

  // Get subject claim from user profile
  const getSubject = (): string => {
    if (oidcAuth.user?.profile.sub) {
      return oidcAuth.user.profile.sub;
    }
    const aud = oidcAuth.user?.profile.aud;
    if (Array.isArray(aud)) {
      return aud[0] || "";
    }
    return aud || "";
  };

  return {
    isAuthenticated: oidcAuth.isAuthenticated || false,
    isLoading: oidcAuth.isLoading || false,
    user: oidcAuth.user
      ? {
          sub: getSubject(),
          sovra_user_id: sovraUserId || null,
          name:
            oidcAuth.user.profile.name ||
            oidcAuth.user.profile.preferred_username ||
            "",
          email: oidcAuth.user.profile.email || "",
          picture: oidcAuth.user.profile.picture,
        }
      : null,

    getAccessTokenSilently: async () => {
      if (!oidcAuth.user) {
        if (oidcAuth.isLoading) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        if (!oidcAuth.user) {
          throw new Error("User not authenticated");
        }
      }

      const user = oidcAuth.user;
      const token = user.access_token;

      if (!token) {
        throw new Error("Access token not available");
      }

      // Validate token format - reject old opaque tokens
      if (!isValidJwtFormat(token)) {
        console.error(
          "[getAccessTokenSilently] Invalid token format detected!",
          {
            tokenPreview: token.substring(0, 50),
            tokenLength: token.length,
          }
        );

        // Clear all OIDC storage to force re-authentication
        if (typeof window !== "undefined") {
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith("oidc.")) {
              localStorage.removeItem(key);
            }
          });
          Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith("oidc.")) {
              sessionStorage.removeItem(key);
            }
          });
        }

        throw new Error(
          "Invalid token format detected. Storage has been cleared. Please refresh the page and log in again."
        );
      }

      // Check if token is expired (with 10 second buffer)
      const expiresAt = user.expires_at;
      if (expiresAt) {
        const now = Math.floor(Date.now() / 1000);
        const buffer = 10;
        if (expiresAt - now < buffer) {
          console.warn(
            "Access token expired or about to expire, attempting refresh..."
          );

          try {
            await new Promise((resolve) => setTimeout(resolve, 500));
            if (oidcAuth.user && oidcAuth.user.access_token) {
              return oidcAuth.user.access_token;
            }
          } catch (refreshError) {
            console.error("Failed to refresh token:", refreshError);
            throw new Error("Access token expired and refresh failed");
          }
        }
      }

      return token;
    },

    loginWithRedirect: async (options?: {
      authorizationParams?: { screen_hint?: string };
    }) => {
      const extraQueryParams: Record<string, string> = {};

      // Add audience if configured (required for OIDC provider to return JWT tokens)
      if (config.oidc?.audience) {
        extraQueryParams.audience = config.oidc.audience;
      }

      // Add screen_hint if provided
      if (options?.authorizationParams?.screen_hint) {
        extraQueryParams.screen_hint = options.authorizationParams.screen_hint;
      }

      await oidcAuth.signinRedirect({
        extraQueryParams:
          Object.keys(extraQueryParams).length > 0
            ? extraQueryParams
            : undefined,
      });
    },

    logout: (options?: { logoutParams?: { returnTo?: string } }) => {
      // Do not send authorize-only params (e.g. audience) on the end-session URL.
      oidcAuth.signoutRedirect({
        post_logout_redirect_uri:
          options?.logoutParams?.returnTo || window.location.origin,
        extraQueryParams: {},
      });
    },

    error: oidcAuth.error || undefined,
  };
};
